import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Volume2, VolumeX, Music, Music2 } from 'lucide-react';
import useAudioStore from '../../store/audioStore';

export const SoundToggle = ({ variant = 'sidebar' }) => {
  const { soundEnabled, musicEnabled, volume, toggleSound, toggleMusic, setVolume, play } = useAudioStore();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      if (spaceRight >= 240) {
        setPos({ top: rect.bottom + 8, left: rect.left, right: 'auto' });
      } else {
        setPos({ top: rect.bottom + 8, left: 'auto', right: Math.max(8, spaceRight) });
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const isMuted = !soundEnabled && !musicEnabled;
  const Icon = isMuted ? VolumeX : Volume2;

  return (
    <div className="relative" data-testid="sound-toggle-wrapper">
      <button aria-label="Son"
        ref={btnRef}
        onClick={() => { setOpen(!open); play('click'); }}
        className="p-2 rounded-lg hover:bg-ink-800/10 transition-colors"
        data-testid="sound-toggle-btn"
        title="Son"
      >
        <Icon className={`w-5 h-5 ${variant === 'sidebar' ? 'text-foreground' : 'text-foreground'}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed w-56 bg-ink-800 rounded-xl shadow-2xl border border-ink-600 z-[100] overflow-hidden"
          style={{ top: pos.top, left: pos.left !== 'auto' ? pos.left : undefined, right: pos.right !== 'auto' ? pos.right : undefined }}
          data-testid="sound-panel"
        >
          <div className="px-4 py-3 border-b border-ink-600">
            <h3 className="text-sm font-bold text-slate-200">Audio</h3>
          </div>

          <div className="p-4 space-y-4">
            {/* Sound Effects Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm text-slate-300">Effets sonores</span>
              </div>
              <button
                onClick={() => { toggleSound(); }}
                className={`w-10 h-5 rounded-full transition-colors relative ${soundEnabled ? 'bg-accent' : 'bg-gray-300'}`}
                data-testid="toggle-sound-effects"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-ink-800 shadow transition-transform ${soundEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Music Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {musicEnabled ? <Music2 className="w-4 h-4 text-foreground" /> : <Music className="w-4 h-4 text-slate-500" />}
                <span className="text-sm text-slate-300">Musique</span>
              </div>
              <button
                onClick={() => { toggleMusic(); }}
                className={`w-10 h-5 rounded-full transition-colors relative ${musicEnabled ? 'bg-accent' : 'bg-gray-300'}`}
                data-testid="toggle-music"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-ink-800 shadow transition-transform ${musicEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {/* Volume Slider */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-500">Volume</span>
                <span className="text-xs text-slate-500 font-mono">{Math.round(volume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={Math.round(volume * 100)}
                onChange={(e) => setVolume(parseInt(e.target.value) / 100)}
                className="w-full h-1.5 bg-ink-700 rounded-full appearance-none cursor-pointer accent-[#1F3864]"
                data-testid="volume-slider"
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SoundToggle;
